"use client";

import React, { useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Send,
  AlertCircle,
  CheckCircle,
  Loader,
  MessageSquare,
  User,
  AtSign,
  Zap,
} from "lucide-react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "success" | "error" | "ratelimit"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.name ||
      !formData.email ||
      !formData.subject ||
      !formData.message
    ) {
      setStatus("error");
      setErrorMessage("Please fill in all fields");
      return;
    }

    if (formData.message.length < 10) {
      setStatus("error");
      setErrorMessage("Message must be at least 10 characters");
      return;
    }

    setLoading(true);
    setStatus("idle");
    setErrorMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.status === 429) {
        setStatus("ratelimit");
        setErrorMessage(
          data.error ||
            "Too many requests. Please wait before sending another message."
        );
      } else if (response.ok) {
        setStatus("success");
        setErrorMessage(
          data.message ||
            "✅ Message sent successfully! I'll get back to you soon."
        );
        setFormData({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => {
          setStatus("idle");
          setErrorMessage("");
        }, 6000);
      } else {
        setStatus("error");
        setErrorMessage(
          data.error || "Failed to send message. Please try again."
        );
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("error");
      setErrorMessage(
        "Connection error. Please try again or email me directly."
      );
    } finally {
      setLoading(false);
    }
  };

  const messageLength = formData.message.length;
  const messagePercentage = (messageLength / 500) * 100;
  const isMessageValid = messageLength >= 10;

  const inputVariants = {
    focus: { scale: 1.01 },
    blur: { scale: 1 },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="relative">
            <label
              htmlFor="name"
              className="block text-sm font-semibold text-foreground mb-2 flex items-center gap-1"
            >
              <User className="w-4 h-4 text-primary" />
              Name <span className="text-rose-500">*</span>
            </label>
            <motion.input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onFocus={() => setFocusedField("name")}
              onBlur={() => setFocusedField(null)}
              placeholder="Your full name"
              disabled={loading}
              variants={inputVariants}
              animate={focusedField === "name" ? "focus" : "blur"}
              className="w-full px-4 py-3 rounded-lg bg-background border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            />
          </div>
        </motion.div>

        {/* Email */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="relative">
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-foreground mb-2 flex items-center gap-1"
            >
              <AtSign className="w-4 h-4 text-primary" />
              Email <span className="text-rose-500">*</span>
            </label>
            <motion.input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onFocus={() => setFocusedField("email")}
              onBlur={() => setFocusedField(null)}
              placeholder="your@email.com"
              disabled={loading}
              variants={inputVariants}
              animate={focusedField === "email" ? "focus" : "blur"}
              className="w-full px-4 py-3 rounded-lg bg-background border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            />
          </div>
        </motion.div>

        {/* Subject */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="relative">
            <label
              htmlFor="subject"
              className="block text-sm font-semibold text-foreground mb-2 flex items-center gap-1"
            >
              <Zap className="w-4 h-4 text-primary" />
              Subject <span className="text-rose-500">*</span>
            </label>
            <motion.input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              onFocus={() => setFocusedField("subject")}
              onBlur={() => setFocusedField(null)}
              placeholder="What would you like to talk about?"
              disabled={loading}
              variants={inputVariants}
              animate={focusedField === "subject" ? "focus" : "blur"}
              className="w-full px-4 py-3 rounded-lg bg-background border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            />
          </div>
        </motion.div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="relative">
            <label
              htmlFor="message"
              className="block text-sm font-semibold text-foreground mb-2 flex items-center gap-1"
            >
              <MessageSquare className="w-4 h-4 text-primary" />
              Message <span className="text-rose-500">*</span>
            </label>
            <motion.textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              onFocus={() => setFocusedField("message")}
              onBlur={() => setFocusedField(null)}
              placeholder="Share your thoughts... (minimum 10 characters)"
              rows={6}
              disabled={loading}
              variants={inputVariants}
              animate={focusedField === "message" ? "focus" : "blur"}
              className="w-full px-4 py-3 rounded-lg bg-background border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            />
            {/* Character counter with progress bar */}
            <motion.div className="mt-3 space-y-2">
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary to-primary/70"
                  initial={{ width: 0 }}
                  animate={{ width: `${messagePercentage}%` }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span
                  className={`font-medium ${
                    isMessageValid
                      ? "text-green-600 dark:text-green-400"
                      : "text-muted-foreground"
                  }`}
                >
                  {messageLength}/500 characters
                </span>
                {isMessageValid && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-green-600 dark:text-green-400 font-medium"
                  >
                    ✓ Ready to send
                  </motion.span>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Status Messages */}
        <AnimatePresence>
          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="flex items-start gap-3 p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/50 dark:to-emerald-950/50 border border-green-200 dark:border-green-800 shadow-lg shadow-green-500/10"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, delay: 0.1 }}
              >
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              </motion.div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                  ✨ Message sent successfully!
                </p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  {errorMessage ||
                    "I'll review your message and get back to you within 24-48 hours."}
                </p>
              </div>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="flex items-start gap-3 p-4 rounded-lg bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-950/50 dark:to-rose-950/50 border border-red-200 dark:border-red-800 shadow-lg shadow-red-500/10"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, delay: 0.1 }}
              >
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              </motion.div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900 dark:text-red-100">
                  {errorMessage}
                </p>
                <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                  Or email me directly at{" "}
                  <a
                    href="mailto:sachinsharma200805@gmail.com"
                    className="font-semibold hover:underline"
                  >
                    sachinsharma200805@gmail.com
                  </a>
                </p>
              </div>
            </motion.div>
          )}

          {status === "ratelimit" && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="flex items-start gap-3 p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50 border border-amber-200 dark:border-amber-800 shadow-lg shadow-amber-500/10"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 400, delay: 0.1 }}
              >
                <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              </motion.div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  ⏱️ Too many requests
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  {errorMessage}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <motion.button
            type="submit"
            disabled={
              loading ||
              !formData.name ||
              !formData.email ||
              !formData.subject ||
              !formData.message ||
              !isMessageValid
            }
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="w-full px-6 py-4 rounded-lg bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-bold hover:from-primary/90 hover:to-primary/70 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-primary disabled:hover:to-primary/80 transition-all flex items-center justify-center gap-2 group shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 disabled:shadow-md disabled:shadow-primary/20 relative overflow-hidden"
          >
            {/* Background glow effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-primary/0 via-white/20 to-primary/0"
              animate={{
                x: loading ? ["-100%", "100%"] : "-100%",
              }}
              transition={{
                duration: 2,
                repeat: loading ? Infinity : 0,
                repeatType: "loop",
              }}
            />

            <div className="relative flex items-center gap-2">
              {loading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <Loader className="w-5 h-5" />
                  </motion.div>
                  <span>Sending message...</span>
                </>
              ) : (
                <>
                  <motion.div
                    animate={{ x: [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Send className="w-5 h-5" />
                  </motion.div>
                  <span>Send Message</span>
                </>
              )}
            </div>
          </motion.button>
        </motion.div>
      </form>
    </motion.div>
  );
}
